import type { Note } from '../song';
import { sizeOf, transpose } from '../score';
import { DREAM_PLAN, KEYS, flavored } from './dream';
import type { Bar, Chord, Ctx } from './dream';

const PENTA = [0, 2, 4, 7, 9] as const;

const CHORDS: Readonly<Record<string, Chord>> = {
  C69: { root: 'C2', pad: ['E3', 'A3', 'D4', 'G4'] },
  Am7: { root: 'A1', pad: ['E3', 'G3', 'C4', 'D4'] },
  Dsus: { root: 'D2', pad: ['A3', 'C4', 'D4', 'G4'] },
  Gsus: { root: 'G1', pad: ['D3', 'A3', 'C4', 'G4'] },
  Em7: { root: 'E2', pad: ['D3', 'G3', 'B3', 'E4'] },
  Fmaj7: { root: 'F1', pad: ['E3', 'A3', 'C4', 'G4'] },
};

const HOOK: Note[][] = [
  [[0, 'E5', 4], [4, 'G5', 2], [6, 'A5', 2], [8, 'G5', 6], [14, 'E5', 2]],
  [[0, 'C6', 3], [3, 'A5', 1], [4, 'G5', 4], [8, 'E5', 4], [12, 'D5', 4]],
  [[0, 'D5', 2], [2, 'E5', 2], [4, 'G5', 4], [8, 'A5', 2], [10, 'C6', 2], [12, 'D6', 4]],
  [[0, 'C6', 4], [4, 'A5', 2], [6, 'G5', 2], [8, 'D5', 8]],
];

const CHIMES = ['C6', 'D6', 'E6', 'G6', 'A6', 'C7'] as const;
const RUN = ['G4', 'A4', 'C5', 'D5', 'E5', 'G5', 'A5', 'C6', 'D6', 'E6', 'G6', 'A6'] as const;
const COUNT: Readonly<Record<string, number>> = { intro: 2, A: 1, drop: 2, drop2: 2, break: 3, outro: 2 };

function extra(x: Ctx, b: Bar): void {
  if (b.end) return;
  const n = COUNT[b.name] ?? 0;
  for (let k = 0; k < n; k++) x.play('chime', b.bar, x.rng.int(0, 16), x.tr(x.rng.pick(CHIMES)), x.s * 8, 0.3 + x.rng.float() * 0.25);
  if (b.drop && b.i === 3) for (const [st, v] of [[10, 0.6], [12, 0.75], [14, 0.9]] as const) x.hit('tom', b.bar, st, v);
}

function gliss(x: Ctx, bar: number, from: number, up: boolean, v0: number, v1: number): void {
  const notes = up ? RUN : [...RUN].reverse();
  notes.forEach((n, k) => x.play('gliss', bar, from + k * 0.5, x.tr(n), x.s * 2, v0 + ((v1 - v0) * k) / (notes.length - 1)));
}

function post(x: Ctx): void {
  const gust = (bar: number) => x.push({ v: 'gust', t: x.g.at(bar), end: x.g.at(bar + 2), bar, step: 0, vel: 1 });
  gust(0);
  gliss(x, x.drop1 - 1, 10, true, 0.4, 0.8);
  if (x.drop2 !== undefined) gliss(x, x.drop2 - 1, 10, true, 0.4, 0.8);
  if (x.brk !== undefined) {
    gust(x.brk);
    gliss(x, x.brk, 0, false, 0.7, 0.35);
    const mid = x.brk + sizeOf(x.form, 'break') / 2;
    gliss(x, mid, 0, false, 0.55, 0.3);
    x.hit('tom', mid, 0, 0.45);
    x.hit('tom', mid, 3, 0.3);
  }
}

export const LANTERNAS = flavored({
  id: 'lanternas',
  genre: 'lantern-festival chill / koto & flute',
  bpm: [110, 124],
  ref: { seed: 'lago', title: 'Lago', params: { bpm: 116, bars: 28, energy: 0.55 } },
  chords: CHORDS,
  progs: [
    ['C69', 'Am7', 'Dsus', 'Gsus'],
    ['Am7', 'Fmaj7', 'C69', 'Gsus'],
    ['C69', 'Em7', 'Am7', 'Dsus'],
    ['Fmaj7', 'C69', 'Dsus', 'Am7'],
  ],
  hook: HOOK,
  arps: [
    [0, 1, 2, 3, 2, 1],
    [3, 2, 1, 0, 1, 2, 3, 1],
    [0, 2, 3, 1, 2, 0, 3, 2],
  ],
  keys: KEYS,
  scale: PENTA,
  range: [72, 88],
  center: 79,
  plan: DREAM_PLAN,
  swing: 54,
  lufs: -12,
  mix: (bpm) => ({ reverbDecay: 4.2, delayTime: (60 / bpm) * 0.75, delayFeedback: 0.3, drive: 1.05, softness: 1.5, pumpDepth: 0.14, pumpRelease: 0.3 }),
  kick: { groove: 'X.......X.......', drop: 'X.....x.X.......' },
  back: { groove: '............x...', drop: '....x.......x...', vel: [0.55, 0.8] },
  hats: ['..x...x...x...x.', 'o-x-o-x-o-x-o-x-', 'x--xx--xx--xx--x'],
  open: { pattern: '', from: 2 },
  bass: {
    groove: [
      [0, 0, 11.5, 0.85],
      [12, 7, 3.5, 0.7],
    ],
    drop: [
      [0, 0, 5.5, 0.9],
      [6, 7, 2, 0.7],
      [8, 0, 5.5, 0.85],
      [14, 12, 2, 0.6],
    ],
  },
  arp: { groove: 'X..x..x.X.......', drop: 'X.xxX.x.X.xxX.x.', len: 2 },
  bells: (pad) => pad.map((n) => transpose(n, 12)),
  extra,
  post,
  accents: { kick: 'kick', snare: 'snare', pad: 'stab', lead: 'melody', impact: 'drop' },
});
